import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setCalendarData, addSchedule, updateSchedule, removeSchedule } from '../store/calendarSlice';
import GravityLogo from './GravityLogo';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { PW_Calendar_API, PW_Products_API, PW_Stores_API, PW_Calendar_Save_API, PW_Calendar_Delete_API } from '../Server/ApiV2';
import Spinner from './Spinner';
import { startingTypeOrder } from '../utils/typeOrder';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function CalendarSettings() {
  const dispatch = useDispatch();
  const { schedules } = useSelector(s => s.calendar);
  const { stores } = useSelector(s => s.admin);
  const { products, productTypes, productTypeLinks } = useSelector(s => s.product);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [calRes, prodRes, storeRes] = await Promise.all([
        PW_Calendar_API(), PW_Products_API(), PW_Stores_API(),
      ]);
      if (cancelled) return;
      if (calRes?.data?.success) dispatch(setCalendarData(calRes.data.data));
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  const activeStores = stores.filter(s => s.status === 'Active');
  const activeProducts = products.filter(p => p.status === 'Active');
  const activeTypes = productTypes.filter(t => t.status === 'Active');

  const emptyForm = {
    dateMode: 'range',
    startDate: '',
    endDate: '',
    storeIds: [],
    productIds: [],
    productTypeOrder: [],
  };

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);
  const [filterStore, setFilterStore] = useState('');
  const [viewStore, setViewStore] = useState(activeStores[0]?.id || '');
  const [viewProduct, setViewProduct] = useState(activeProducts[0]?.id || '');
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getSchedulesForDay = (storeId, productId, dateStr) => {
    const active = schedules.filter(s => s.status === 'Active' && s.storeIds.includes(storeId)
      && (!productId || !s.productIds?.length || s.productIds.includes(productId)));
    return active.filter(s => {
      const start = (s.startDate || '').slice(0, 10);
      const end = (s.endDate || '').slice(0, 10);
      return dateStr >= start && dateStr <= end;
    });
  };

  const viewLinkedTypes = viewProduct
    ? productTypeLinks.filter(l => l.linked && l.productId === viewProduct).map(l => l.typeId)
    : null;

  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const viewDays = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const dateStr = `${viewYear}-${String(viewMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dateObj = new Date(dateStr + 'T00:00');
    const dayName = DAYS[dateObj.getDay()];
    const daySchedules = viewStore ? getSchedulesForDay(viewStore, viewProduct, dateStr) : [];
    return { day, dateStr, dayName, schedules: daySchedules };
  });

  const linkedTypeIds = form.productIds.length > 0
    ? [...new Set(productTypeLinks.filter(l => l.linked && form.productIds.includes(l.productId)).map(l => l.typeId))]
    : activeTypes.map(t => t.id);
  // Show every linked type as an add button — a type may be added more than once so a
  // schedule can run it multiple times per cycle (e.g. L1 -> L2 -> L1 -> N1).
  const availableTypes = activeTypes.filter(t => linkedTypeIds.includes(t.id));

  const isValid = form.storeIds.length > 0 && form.productIds.length > 0 && form.productTypeOrder.length > 0
    && form.startDate && form.endDate && form.startDate <= form.endDate;

  const resetForm = () => {
    setForm(emptyForm); setEditing(null); setFormError('');
  };

  const handleSelectStore = (storeId) => {
    setForm(prev => ({ ...prev, storeIds: [storeId] }));
  };

  const handleSelectProduct = (productId) => {
    setForm(prev => {
      const newIds = prev.productIds.includes(productId) ? [] : [productId];
      const linkedTypes = newIds.length > 0
        ? productTypeLinks.filter(l => l.linked && newIds.includes(l.productId)).map(l => l.typeId)
        : [];
      return { ...prev, productIds: newIds, productTypeOrder: startingTypeOrder(newIds[0], linkedTypes) };
    });
  };

  const handleAddType = (typeId) => {
    setForm(prev => ({ ...prev, productTypeOrder: [...prev.productTypeOrder, typeId] }));
  };

  const handleRemoveType = (index) => {
    setForm(prev => ({ ...prev, productTypeOrder: prev.productTypeOrder.filter((_, i) => i !== index) }));
  };

  const handleMoveType = (index, direction) => {
    setForm(prev => {
      const order = [...prev.productTypeOrder];
      const target = index + direction;
      if (target < 0 || target >= order.length) return prev;
      [order[index], order[target]] = [order[target], order[index]];
      return { ...prev, productTypeOrder: order };
    });
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    setFormError('');
    const conflict = schedules.find(s => {
      if (editing && s.id === editing) return false;
      if (!s.storeIds.some(sid => form.storeIds.includes(sid))) return false;
      if (form.productIds.length && s.productIds?.length && !s.productIds.some(pid => form.productIds.includes(pid))) return false;
      const sStart = (s.startDate || '').slice(0, 10);
      const sEnd = (s.endDate || '').slice(0, 10);
      return form.startDate <= sEnd && form.endDate >= sStart;
    });

    const schedData = editing
      ? { id: editing, ...form }
      : { id: `CAL-${Date.now()}`, ...form, status: 'Active', createdAt: new Date().toISOString() };

    // Persist FIRST and only mutate local state on success — a failed save must not
    // leave the UI showing a "saved" schedule that the server actually rejected.
    setSaving(true);
    const res = await PW_Calendar_Save_API(schedData);
    setSaving(false);
    if (!res?.data?.success) {
      setFormError('Could not save the schedule — please try again.');
      return;
    }

    if (conflict) {
      dispatch(removeSchedule(conflict.id));
      PW_Calendar_Delete_API(conflict.id).catch(err => console.error('[Calendar] Conflict delete failed:', err));
    }
    if (editing) dispatch(updateSchedule(schedData));
    else dispatch(addSchedule(schedData));
    resetForm();
  };

  const handleEdit = (sched) => {
    setEditing(sched.id);
    const pid = (sched.productIds || [])[0];
    let typeOrder = [...(sched.productTypeOrder || [])];
    // Self-heal: a legacy/empty schedule gets its Type Hierarchy re-populated from the
    // product's linked types so it is never shown (or re-saved) with a missing hierarchy.
    if (typeOrder.length === 0 && pid) {
      typeOrder = productTypeLinks.filter(l => l.linked && l.productId === pid).map(l => l.typeId);
    }
    setForm({
      dateMode: 'range',
      startDate: (sched.startDate || '').slice(0, 10),
      endDate: (sched.endDate || '').slice(0, 10),
      storeIds: [...sched.storeIds],
      productIds: [...(sched.productIds || [])],
      productTypeOrder: typeOrder,
    });
    if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const formatDate = (sched) => {
    const toDate = (v) => new Date(typeof v === 'string' && v.includes('T') ? v : v + 'T00:00');
    const s = toDate(sched.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = toDate(sched.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} — ${e}`;
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Calendar Settings</h2>
          <div className="page-header-sub">Schedule product types for stores by date</div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-title gold">Store Schedule View</div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 16 }}>
            <div style={{ flex: 1, maxWidth: 220 }}>
              <div className="text-sm text-muted mb-8">Store</div>
              <select className="form-select" value={viewStore}
                onChange={e => setViewStore(e.target.value)}>
                {activeStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, maxWidth: 180 }}>
              <div className="text-sm text-muted mb-8">Product</div>
              <select className="form-select" value={viewProduct}
                onChange={e => setViewProduct(e.target.value)}>
                {activeProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-sm text-muted mb-8">Month</div>
              <select className="form-select" value={viewMonth}
                onChange={e => setViewMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <div className="text-sm text-muted mb-8">Year</div>
              <select className="form-select" value={viewYear}
                onChange={e => setViewYear(Number(e.target.value))}>
                {[2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="table-wrap cal-view-wrap">
            <table className="data-table cal-view-table">
              <thead>
                <tr>
                  <th style={{ width: 46 }}>Date</th>
                  <th style={{ width: 40 }}>Day</th>
                  <th>Product Types</th>
                  <th style={{ width: 90 }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {viewDays.map(({ day, dayName, schedules: dayScheds }) => {
                  const isWeekend = dayName === 'Sat' || dayName === 'Sun';
                  return (
                    <tr key={day} className={isWeekend ? 'cal-weekend' : ''}>
                      <td data-label="Date" className="fw-600">{day}</td>
                      <td data-label="Day" className={isWeekend ? 'fw-600' : 'text-muted'}>{dayName}</td>
                      <td data-label="Product Types">
                        {dayScheds.length > 0 ? (
                          dayScheds.map((sched, si) => {
                            const types = sched.productTypeOrder.filter(tid => !viewLinkedTypes || viewLinkedTypes.includes(tid));
                            return (
                              <div key={sched.id} style={{ marginBottom: si < dayScheds.length - 1 ? 4 : 0 }}>
                                {types.map((tid, i) => (
                                  <React.Fragment key={`${tid}-${i}`}>
                                    {i > 0 && <span className="cal-arrow">→</span>}
                                    <span className="chip chip-purple" style={{ fontSize: 11 }}>{tid}</span>
                                  </React.Fragment>
                                ))}
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td data-label="Source">
                        {dayScheds.length > 0 && dayScheds.map((sched, si) => (
                          <div key={sched.id} style={{ marginBottom: si < dayScheds.length - 1 ? 4 : 0 }}>
                            <span className="chip chip-purple" style={{ fontSize: 10 }}>Range</span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card mb-24" ref={formRef}>
        <div className="card-title purple">{editing ? 'Edit Schedule' : 'New Schedule'}</div>
        <div className="card-body">

          <div className="cal-section">
            <div className="cal-label">Date Range</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <div className="text-sm text-muted mb-8">From</div>
                <input type="date" className="form-input" value={form.startDate}
                  style={{ width: 180 }}
                  onChange={e => setForm(prev => ({ ...prev, startDate: e.target.value }))} />
              </div>
              <div>
                <div className="text-sm text-muted mb-8">To</div>
                <input type="date" className="form-input" value={form.endDate}
                  min={form.startDate || undefined}
                  style={{ width: 180 }}
                  onChange={e => setForm(prev => ({ ...prev, endDate: e.target.value }))} />
              </div>
            </div>
          </div>

          <div className="cal-section">
            <div className="cal-label">Stores</div>
            <div className="cal-store-grid">
              {activeStores.map(store => (
                <label key={store.id} className={`cal-store-check${form.storeIds.includes(store.id) ? ' selected' : ''}`}>
                  <input type="radio" name="cal-store"
                    checked={form.storeIds.includes(store.id)}
                    onChange={() => handleSelectStore(store.id)} />
                  <span>{store.name}</span>
                  <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>{store.code}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="cal-section">
            <div className="cal-label">Product</div>
            <div className="cal-store-grid">
              {activeProducts.map(p => (
                <label key={p.id} className={`cal-store-check${form.productIds.includes(p.id) ? ' selected' : ''}`}>
                  <input type="radio" name="cal-product"
                    checked={form.productIds.includes(p.id)}
                    onChange={() => handleSelectProduct(p.id)} />
                  <span>{p.name}</span>
                  <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>RSP {p.rsp}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="cal-section">
            <div className="cal-label">Product Type Hierarchy</div>
            <div className="text-sm text-muted mb-8">Click types to add in priority order:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {availableTypes.map(t => (
                <button key={t.id} className="btn btn-outline btn-sm" onClick={() => handleAddType(t.id)}>
                  + {t.name}
                </button>
              ))}
            </div>

            {form.productTypeOrder.length > 0 && (() => {
              const prod = products.find(pr => form.productIds.includes(pr.id));
              const factor = prod?.priceAmount || 1;
              const scale = prod ? 100 / prod.cardsPerBundle : 1;
              const isAny2 = prod && (prod.matchingMethod || prod.winCondition) === 'any2';
              const selling = prod ? prod.rsp * 100 : 0;
              let grandPrize = 0;
              const typeStats = form.productTypeOrder.map(typeId => {
                const link = productTypeLinks.find(l => l.productId === prod?.id && l.typeId === typeId && l.linked);
                const config = link?.winnerConfig || {};
                const winners = Object.values(config).reduce((s, v) => s + (v || 0), 0);
                const configPrize = Object.entries(config).reduce((s, [d, c]) => s + Number(d) * (c || 0), 0) * factor;
                const scaledPrize = Math.round(configPrize * scale);
                const scaledWinners = Math.round(winners * scale);
                const minGuarantee = isAny2 ? (100 - scaledWinners) * 20 * factor : 0;
                const totalPrize = scaledPrize + minGuarantee;
                grandPrize += totalPrize;
                return { typeId, totalPrize, configPrize: scaledPrize, minGuarantee };
              });
              const grandPnL = selling * form.productTypeOrder.length - grandPrize;
              return (
              <div>
                <div className="cal-type-order">
                  {form.productTypeOrder.map((typeId, idx) => {
                    const type = activeTypes.find(t => t.id === typeId);
                    const stats = typeStats[idx];
                    return (
                      <div key={`${typeId}-${idx}`} className="cal-type-item" style={{ flexWrap: 'wrap' }}>
                        <span className="cal-type-num">{idx + 1}</span>
                        <span className="fw-600">{type?.name || typeId}</span>
                        <span className="text-sm text-muted">{type?.label || ''}</span>
                        <div style={{ display: 'flex', gap: 12, marginLeft: 12 }}>
                          <span className="text-sm" style={{ color: '#2d1b69' }}>Sell: <strong>{selling.toLocaleString()}</strong></span>
                          <span className="text-sm" style={{ color: '#c9a227' }}>Prize: <strong>{stats.totalPrize.toLocaleString()}</strong></span>
                          <span className="text-sm" style={{ color: (selling - stats.totalPrize) >= 0 ? '#00895e' : '#e74c3c' }}>
                            P&L: <strong>{(selling - stats.totalPrize).toLocaleString()}</strong>
                          </span>
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm btn-secondary cal-move-btn"
                            disabled={idx === 0} onClick={() => handleMoveType(idx, -1)}>↑</button>
                          <button className="btn btn-sm btn-secondary cal-move-btn"
                            disabled={idx === form.productTypeOrder.length - 1} onClick={() => handleMoveType(idx, 1)}>↓</button>
                          <button className="btn btn-sm cal-remove-btn"
                            onClick={() => handleRemoveType(idx)}>×</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {prod && form.productTypeOrder.length > 0 && (
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, padding: '8px 12px', background: '#f5f3fa', borderRadius: 6, fontSize: 13 }}>
                    <span className="fw-600" style={{ color: '#2d1b69' }}>
                      Total ({form.productTypeOrder.length} types × 100):
                    </span>
                    <span style={{ color: '#2d1b69' }}>Sell: <strong>{(selling * form.productTypeOrder.length).toLocaleString()}</strong></span>
                    <span style={{ color: '#c9a227' }}>Prize: <strong>{grandPrize.toLocaleString()}</strong></span>
                    <span style={{ color: grandPnL >= 0 ? '#00895e' : '#e74c3c' }}>P&L: <strong>{grandPnL.toLocaleString()}</strong></span>
                  </div>
                )}
              </div>
              );
            })()}
          </div>

          {formError && (
            <div style={{ color: '#c62828', background: '#fef2f2', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginTop: 8 }}>
              {formError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-success" disabled={!isValid || saving} onClick={handleSave}>
              {saving ? 'Saving…' : (editing ? 'Update Schedule' : 'Save Schedule')}
            </button>
            {editing && <button className="btn btn-secondary" onClick={resetForm}>Cancel</button>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">
          Saved Schedules
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b6b80' }}>
            {filterStore ? schedules.filter(s => s.storeIds.includes(filterStore)).length + ' of ' : ''}{schedules.length} schedules
          </span>
        </div>
        {schedules.length > 0 && (
          <div style={{ padding: '8px 16px' }}>
            <select className="form-select" value={filterStore} onChange={e => setFilterStore(e.target.value)} style={{ maxWidth: 250 }}>
              <option value="">All Stores</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <div className="card-body">
          {schedules.length === 0 ? (
            <div className="empty-state"><GravityLogo size={48} circle /><p>No schedules configured yet</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {schedules.filter(s => !filterStore || s.storeIds.includes(filterStore)).map(sched => (
                <div key={sched.id} className="cal-schedule-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <span className="fw-700" style={{ fontSize: 15, color: '#2d1b69' }}>{formatDate(sched)}</span>
                      <span className="chip chip-purple" style={{ marginLeft: 8 }}>Date Range</span>
                    </div>
                    <span className={`chip ${sched.status === 'Active' ? 'chip-green' : 'chip-red'}`}>{sched.status}</span>
                  </div>
                  <div className="mb-8">
                    <span className="text-sm text-muted">Date Range: </span>
                    <span className="text-sm fw-600">
                      {(sched.startDate || '').slice(0, 10)} &nbsp;→&nbsp; {(sched.endDate || '').slice(0, 10)}
                    </span>
                  </div>
                  <div className="mb-8">
                    <span className="text-sm text-muted">Stores: </span>
                    <span className="text-sm fw-600">
                      {sched.storeIds.map(sid => stores.find(s => s.id === sid)?.name || sid).join(', ')}
                    </span>
                  </div>
                  <div className="mb-8">
                    <span className="text-sm text-muted">Products: </span>
                    {(sched.productIds || []).map((pid, i) => (
                      <React.Fragment key={pid}>
                        {i > 0 && <span style={{ margin: '0 4px' }}>·</span>}
                        <span className="text-sm fw-600">{products.find(p => p.id === pid)?.name || pid}</span>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="mb-8">
                    <span className="text-sm text-muted">Type Hierarchy: </span>
                    {(sched.productTypeOrder || []).length === 0 && (
                      <span className="chip chip-red" style={{ fontSize: 11 }}>Not set — open Edit to populate</span>
                    )}
                    {sched.productTypeOrder.map((tid, i) => {
                      const t = productTypes.find(pt => pt.id === tid);
                      return (
                        <React.Fragment key={`${tid}-${i}`}>
                          {i > 0 && <span className="cal-arrow">→</span>}
                          <span className="chip chip-purple" style={{ fontSize: 11 }} title={t?.name || tid}>
                            {tid}{(t?.label || t?.name) ? ` · ${t.label || t.name}` : ''}
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => handleEdit(sched)}>Edit</button>
                    <button className="btn btn-sm cal-remove-btn" onClick={() => {
                      dispatch(removeSchedule(sched.id));
                      PW_Calendar_Delete_API(sched.id).catch(err => console.error('[Calendar] Delete failed:', err));
                    }}>Delete</button>
                  </div>
                </div>
              ))}
              {filterStore && schedules.filter(s => s.storeIds.includes(filterStore)).length === 0 && (
                <div style={{ textAlign: 'center', padding: 24, color: '#6b6b80' }}>No schedules for this store</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
